/**
 * Accent Color Utilities
 *
 * Utilities for working with the project's accent color,
 * including contrast detection for accessibility.
 */

/**
 * Get the current accent color from CSS custom properties
 * @returns {string} The accent color in hex format
 */
export function getAccentColor() {
  const style = getComputedStyle(document.documentElement);
  return style.getPropertyValue('--accent-color').trim() || '#009be5';
}

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color string (with or without #)
 * @returns {object} Object with r, g, b values (0-255)
 */
export function hexToRgb(hex) {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Handle shorthand hex (e.g., #fff)
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  const bigint = parseInt(hex, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  };
}

/**
 * Calculate relative luminance of a color
 * Based on WCAG 2.1 formula
 * @param {object} rgb - Object with r, g, b values (0-255)
 * @returns {number} Luminance value between 0 and 1
 */
export function getLuminance({ r, g, b }) {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Check if a color is considered "light" (needs dark text/icons)
 * @param {string} hex - Hex color string
 * @param {number} threshold - Luminance threshold (default 0.5)
 * @returns {boolean} True if the color is light
 */
export function isLightColor(hex, threshold = 0.5) {
  const rgb = hexToRgb(hex);
  const luminance = getLuminance(rgb);
  return luminance > threshold;
}

/**
 * Check if the current accent color is light
 * @returns {boolean} True if the accent color is light
 */
export function isAccentColorLight() {
  const accentColor = getAccentColor();
  return isLightColor(accentColor);
}

/**
 * Apply contrast-aware styling to checkboxes
 * Adds .checkbox-dark-check class to checkboxes when accent color is light
 * @param {Element|Document} root - Root element to search within (default: document)
 */
export function applyContrastAwareCheckboxes(root = document) {
  const needsDarkCheck = isAccentColorLight();
  const checkboxes = root.querySelectorAll('input[type="checkbox"]');

  checkboxes.forEach(checkbox => {
    if (needsDarkCheck) {
      checkbox.classList.add('checkbox-dark-check');
    } else {
      checkbox.classList.remove('checkbox-dark-check');
    }
  });
}

/**
 * Initialize contrast-aware checkboxes with mutation observer
 * Automatically applies styling to new checkboxes added to the DOM
 */
export function initContrastAwareCheckboxes() {
  // Apply to existing checkboxes
  applyContrastAwareCheckboxes();

  // Watch for new checkboxes being added
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if the node itself is a checkbox
          if (node.matches && node.matches('input[type="checkbox"]')) {
            if (isAccentColorLight()) {
              node.classList.add('checkbox-dark-check');
            }
          }
          // Check for checkboxes within the added node
          if (node.querySelectorAll) {
            applyContrastAwareCheckboxes(node);
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  return observer;
}

/**
 * Get contrasting text color for a given background
 * @param {string} hex - Background color in hex
 * @returns {string} Either '#ffffff' or '#171717' for best contrast
 */
export function getContrastingTextColor(hex) {
  return isLightColor(hex) ? '#171717' : '#ffffff';
}

export default {
  getAccentColor,
  hexToRgb,
  getLuminance,
  isLightColor,
  isAccentColorLight,
  applyContrastAwareCheckboxes,
  initContrastAwareCheckboxes,
  getContrastingTextColor
};
